import { Injectable, Inject } from '@angular/core';
import { HttpService, AuthenticationService } from 'ew-common-lib';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HttpHeaders } from '@angular/common/http';
import { imagesApiUrls, awsPreSignedUrl } from './constants/api-urls';

@Injectable({
  providedIn: 'root',
})
export class EwMaImageService {
  /**
   * @description
   * API token for authentication.
   */
  private token: string;

  /**
   * @description
   * Event id to use in the library
   */
  public eventId: string;

  /**
   * @description
   * environment file for API base URLs
   */
  public env;

  /**
   * @description
   * Inject services to use in this component.
   * @param env environment file for API base URLs.
   * @param httpService http service to call APIs.
   * @param authenticationService To get auth token.
   */
  constructor(
    // @Inject('env') private env,
    private httpService: HttpService,
    private authenticationService: AuthenticationService
  ) {
    this.token = this.authenticationService.getUserAccessToken();
  }

  /**
   * @description
   * Generates full url for API request.
   * @param url Resource name
   */
  getUrl(url: string) {
    return this.env.apiBaseUrl + url;
  }

  getS3BucketUrl() {
    return this.env.awsS3BucketUrl;
  }

  getResourceUrl(str: string){
    return this.env.V2CDN + str;
  }

  // Amazon Upload Request Header To Make Upload Downloadable
  private setAwsHeaders(type: string): HttpHeaders {
    const headersConfig = {
      'Content-Type': type,
    };
    return new HttpHeaders(headersConfig);
  }

  uploadAWSObject(route, data): Observable<any> {
    return this.httpService
      .put(this.token, route, data, this.setAwsHeaders(data.type))
      .pipe(catchError(this.handleError<any>('uploadAWSObject')));
  }

  getSignedAWSResponseUrl(data): Observable<any> {
    //let url = this.getUrl(awsPreSignedUrl.v1.url);
    let url = this.env.awsS3SignedURL+ 's3-presigned-url';
    
    return this.httpService
      .post(this.token, url, data)
      .pipe(catchError(this.handleError<any>('getSignedAWSResponseUrl')));
  }

  getImageMeta(file: any) {
    return new Promise((resolve, reject) => {
      let fr = new FileReader();
      fr.onload = () => {
        // when file has loaded
        var img = new Image();
        img.onload = () => {
          resolve({ width: img.width, height: img.height, size: file.size });
        };
        img.src = fr.result as string; // This is the data URL
      };
      fr.readAsDataURL(file);
    }).catch((error) => {
      console.log('Error', error);
      return { width: 0, height: 0, size: 0 };
    });
  }

  /**
   * @description
   * Service to fetch gallery images
   * @returns API response as observable.
   */
  getImages(model: any) {
    let url = this.getUrl(imagesApiUrls.v1.getImages);
    return this.httpService
      .get(this.token, url, model)
      .pipe(catchError(this.handleError<any>('getImages')));
  }

  /**
   * @description
   * Service to add gallery image
   * @param model Data object to pass in header
   */
  addImage(model: any) {
    let url = this.getUrl(imagesApiUrls.v1.addImage);
    return this.httpService
      .post(this.token, url, model)
      .pipe(catchError(this.handleError<any>('addImage')));
  }

  /**
   * @description
   * Service to fetch unsplash images
   * @returns API response as observable.
   */
  getUnsplashImages(model: any) {
    let url = `https://api.unsplash.com/photos/?client_id=2KFnjqvR0xBd0Z3vy57J5egbBvDzUpcX1MjaU8AP66k`;
    return this.httpService
      .get(this.token, url, model)
      .pipe(catchError(this.handleError<any>('getUnsplashImages')));
  }

  /**
   * @description
   * Service to search unsplash images
   * @returns API response as observable.
   */
  searchUnsplashImages(model: any) {
    let url = `https://api.unsplash.com/search/photos/?client_id=2KFnjqvR0xBd0Z3vy57J5egbBvDzUpcX1MjaU8AP66k`;
    return this.httpService
      .get(this.token, url, model)
      .pipe(catchError(this.handleError<any>('searchUnsplashImages')));
  }

  /**
   * @description
   * Handle error
   * @param string operation
   * @param T result
   */
  handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      // TODO: send the error to remote logging infrastructure

      // TODO: better job of transforming error for user consumption
      console.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return throwError(error);
    };
  }
}
